"use client";

import type { RefObject } from "react";

import { useLayoutEffect, useRef } from "react";

/**
 * Keeps a section takeover in view across step changes. The takeover hides
 * the copy above the flow, so a visitor scrolled into the section would
 * otherwise be left past the step's top, under the sticky header. The scroll
 * runs in the step change's commit, so the view transition captures the new
 * step already in place rather than jumping to it afterwards. The section's
 * scroll margin clears the sticky header.
 */
function useSectionTakeoverScroll(
  containerRef: RefObject<HTMLDivElement | null>,
  stepKey: string | undefined,
  isTakingOverSection: boolean,
) {
  const previousStepKeyRef = useRef(stepKey);

  useLayoutEffect(() => {
    if (previousStepKeyRef.current === stepKey) {
      return;
    }

    previousStepKeyRef.current = stepKey;
    const section = containerRef.current?.closest("section");
    if (!isTakingOverSection || !section) {
      return;
    }

    const scrollMarginTop =
      Number.parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    if (section.getBoundingClientRect().top < scrollMarginTop) {
      section.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [containerRef, isTakingOverSection, stepKey]);
}

export { useSectionTakeoverScroll };
